import React, { useMemo } from 'react';
import { router } from '@inertiajs/react';
import SignupLayout from '../Layout.jsx';
import PaymentForm, { FreeOrderConfirm } from '../../../components/PaymentForm.jsx';
import LiveCohortPolicy from '../../../components/LiveCohortPolicy.jsx';
import { formatPrice } from '../../../constants/reviewVariants.js';
import { primaryBtnClass, ITEM_DEFS } from '../shared.jsx';

const LIVE_COHORT_ITEM_KEY = 'Administrator|recertification_20hr_live';

// Promo codes are individual-buyer only — group carts get volume discounts
// instead. The server enforces this too; there is no promo entry here.
export default function GroupCheckout({
  client_secret, publishable_key, return_url, mode, amount_cents,
  subtotal_cents, volume_discount_cents,
  seats, plan, progress,
}) {
  const selectedItems = useMemo(() => ITEM_DEFS.filter((d) => (seats?.[d.key] || 0) > 0), [seats]);
  const hasLiveSeat = (seats?.[LIVE_COHORT_ITEM_KEY] || 0) > 0;
  const hasBreakdown = subtotal_cents != null;
  const showVolume = (volume_discount_cents || 0) > 0;

  return (
    <SignupLayout progress={progress}>
      <div className="space-y-5">
        <div>
          <h2 className="text-2xl/9 font-bold tracking-tight text-gray-900">Complete your purchase</h2>
          <p className="text-sm text-gray-600">Securely complete payment below. You won't leave this page.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <div className="rounded-lg border border-gray-200 bg-white p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-800">Order summary</h3>
                <button
                  type="button"
                  onClick={() => router.visit('/signup/group/training')}
                  className="text-sm font-medium text-indigo-600 hover:text-indigo-700 cursor-pointer"
                >
                  Edit
                </button>
              </div>
              <div className="divide-y divide-gray-100">
                {selectedItems.map((item) => (
                  <div key={item.key} className="py-2 flex justify-between gap-3 text-sm">
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 break-words">{item.role} {item.label}</p>
                      <p className="text-xs text-gray-500">{seats[item.key]} {seats[item.key] === 1 ? 'seat' : 'seats'}</p>
                    </div>
                  </div>
                ))}
              </div>

              {hasBreakdown && (
                <div className="mt-3 pt-3 border-t border-gray-200 space-y-1.5 text-sm">
                  <div className="flex justify-between text-gray-700">
                    <span>Subtotal</span><span>{formatPrice(subtotal_cents)}</span>
                  </div>
                  {showVolume && (
                    <div className="flex justify-between font-medium text-green-700">
                      <span>Volume discount</span><span>−{formatPrice(volume_discount_cents)}</span>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-3 pt-3 border-t border-gray-200 flex justify-between items-center">
                <span className="text-sm font-semibold text-gray-900">{mode === 'subscription' ? 'Due today' : 'Total'}</span>
                <span className="text-lg font-bold text-indigo-600">{formatPrice(amount_cents)}</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="rounded-lg border border-gray-200 bg-white p-5">
              {client_secret ? (
                <PaymentForm
                  clientSecret={client_secret}
                  publishableKey={publishable_key}
                  returnUrl={return_url}
                  submitLabel={`Pay ${formatPrice(amount_cents)}`}
                />
              ) : (
                <FreeOrderConfirm returnUrl={return_url} />
              )}
            </div>
            {hasLiveSeat && (
              <div className="mt-4">
                <LiveCohortPolicy />
              </div>
            )}
          </div>
        </div>
      </div>
    </SignupLayout>
  );
}
